import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { randomId } from "@mui/x-data-grid-generator";
import { Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector, useDispatch } from "react-redux";
import {
  addDeployment,
  addDevice,
  deleteDeployment,
  deleteDevice,
  updateDeployment,
  updateDevice,
} from "../actions/adminAction";

// Add Functionality
function EditToolbar(props) {
  const { setRows, setRowModesModel } = props;

  const handleClick = () => {
    const id = randomId();
    setRows((prev) => [{ id, title: "", type: "", isNew: true }, ...prev]);
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "title" },
    }));
  };

  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        Add Device
      </Button>
    </GridToolbarContainer>
  );
}

export default function DeviceGridTable({ setActiveTab }) {
  const dispatch = useDispatch();
  const devicesList = useSelector((state) => state.admin.devicesList);
  const [deleteID, setDeleteID] = useState();
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});

  // Re-render when there is update on the deployment list
  useEffect(() => {
    if (devicesList && devicesList.data) {
      const initialRows = devicesList.data.map((device) => ({
        id: device.deviceID,
        deviceID: device.deviceID,
        title: device.title,
        type: device.type,
      }));
      setRows(initialRows);
    }
  }, [devicesList.data]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    dispatch(deleteDevice(id));
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow && editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow) => {
    if (newRow.isNew) {
      dispatch(addDevice(newRow));
      return newRow;
    } else {
      const updatedRow = { ...newRow, isNew: false };
      setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

      dispatch(updateDevice(updatedRow.deviceID, updatedRow));
      return updatedRow;
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const deviceColumns = [
    // {
    //   field: "deviceID",
    //   headerName: "Device ID",
    //   width: 150,
    //   editable: false,
    // },
    {
      field: "title",
      headerName: "Title",
      width: 150,
      editable: true,
    },
    {
      field: "type",
      headerName: "Device Type",
      type: "singleSelect",
      width: 150,
      editable: true,
      valueOptions: ["Stationary", "AQI", "Robot", "People Count - In/Out"],
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              sx={{
                color: "primary.secondary",
              }}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            data-bs-toggle="modal"
            data-bs-target="#deleteUser"
            onClick={() => setDeleteID(id)}
            sx={{
              color: "#dd3545",
            }}
          />,
        ];
      },
    },
  ];

  return (
    <Box
      sx={{
        height: 400,
        width: "100%",
      }}
    >
      {/* Delete Modal */}
      <div
        className="modal fade"
        id="deleteUser"
        aria-labelledby="deleteUserLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content admin-list">
            <div className="modal-header">
              <h5 className="modal-title" id="deleteUserLabel">
                Delete Device
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete Device {`${deleteID}`}?
              <br />
              <br />
              <b>
                <span className="text-danger text-center">
                  Warning: Delete device will impact the data display on the portal.
                </span>
              </b>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn admin-list-btn-secondary text-white"
                onClick={handleDeleteClick(deleteID)}
                data-bs-dismiss="modal"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>

      {rows && rows.length > 0 && devicesList ? (
        <DataGrid
          sx={{
            color: "white",
            "& .MuiDataGrid-menuIconButton": {
              color: "white",
            },
            "& .MuiCheckbox-root": {
              color: "white",
            },

            "& .MuiDataGrid-sortIcon": {
              color: "white",
            },

            "& .MuiTablePagination-root": {
              bgcolor: "white",
            },
            "& .MuiDataGrid-cell": {
              border: 0,
            },
            "& .MuiDataGrid-row:hover": {
              bgcolor: "primary.main",
            },
            "& .MuiDataGrid-cell:hover": {
              bgcolor: "none",
            },
            "& .MuiDataGrid-cell:focus": {
              bgcolor: "primary.light",
            },
          }}
          rows={rows}
          columns={deviceColumns}
          editMode="row"
          getRowId={(row) => row.id}
          // initialState={{
          //   pagination: {
          //     paginationModel: {
          //       pageSize: 5,
          //     },
          //   },
          // }}
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          pageSizeOptions={[5]}
          // checkboxSelection
          // disableRowSelectionOnClick
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: { setRows, setRowModesModel },
          }}
        />
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
}
